"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Lead = void 0;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const FetchActions_1 = require("./FetchActions");
const utils_1 = require("../common/utils");
class Lead {
    constructor(client, { campaign, initialState = {}, requiredPartialFields = [], test = false, events, } = {}) {
        this._meta = {
            leadId: "",
            postedDt: null,
            testLead: false,
            approved: false,
        };
        this.requiredPartialFields = [];
        this.actionQueue = new Set();
        this.results = [];
        this.resultIndex = 0;
        this.processor = Promise.resolve([]);
        /**
         * Get the value of a given lead field.
         */
        this.get = (key) => {
            return this._state[key];
        };
        /**
         * Set the value of a given lead field.
         */
        this.set = (key, value) => {
            this._state[key] = this.events.onFieldUpdate(key, value);
            this.events.onUpdate(this._state);
            return this.fields;
        };
        /**
         * Process a single payload.
         */
        this.processPayload = async (action) => {
            var _a, _b;
            /* merge campaign data & lpTest field into payload */
            const payload = Object.assign(action.payload, {
                lpCampaignID: (_a = this.campaign) === null || _a === void 0 ? void 0 : _a.postId,
                lpCampaignKey: (_b = this.campaign) === null || _b === void 0 ? void 0 : _b.postKey,
            }, {
                lpTest: this._meta.testLead ? "1" : "0",
                lpResponse: "JSON",
            });
            try {
                const response = this._meta.leadId
                    ? await this.actions.update(payload, action.updateOptions)
                    : await this.actions.post(payload);
                const isNewLead = (response && response.leadId) &&
                    !this._meta.leadId;
                if (isNewLead) {
                    this._meta.leadId = response.leadId;
                    this._meta.postedDt = new Date();
                }
                this.results.push({
                    type: "success",
                    data: response,
                });
                return true;
            }
            catch (err) {
                /* process caught error and add to this.results */
                if (err instanceof Error) {
                    this.results.push({
                        type: "error",
                        data: {
                            message: err.message,
                            stack: err.stack,
                            originalError: err,
                        },
                    });
                }
                else {
                    (typeof err === "object") ? (this.results.push({
                        type: "error",
                        data: {
                            message: err.message || err.value || "Unknown Error",
                            originalError: err,
                        },
                    })) : (this.results.push({
                        type: "error",
                        data: {
                            message: String(err),
                            originalError: err,
                        },
                    }));
                }
                return false;
            }
        };
        /**
         * Process a snapshot of the action queue.
         */
        this.processQueue = () => {
            const queue = Array.from(this.actionQueue);
            queue.forEach((action) => {
                this.actionQueue.delete(action);
            });
            this.processor = this.processor.then(async () => {
                for (const action of queue) {
                    await this.processPayload(action);
                }
                return [];
            });
        };
        /**
         * Returns a promise that can be awaited, signalling the end of all queued actions.
         *
         * @param {boolean} all - Should the entire results collection be returned?
         * default: `false`
         *
         * If `false`, only the latest result will be returned.
         */
        this.promise = (all = false) => {
            return (this.processor = this.processor.then(() => {
                const resultIndex = this.resultIndex;
                this.resultIndex = this.results.length - 1;
                const results = this.results.slice(resultIndex);
                if (!all) {
                    return results.pop();
                }
                return results;
            }));
        };
        this.addAction = (updateOptions) => {
            const payload = (0, cloneDeep_1.default)(this._state);
            this.actionQueue.add({ payload, updateOptions });
            this.processQueue();
            return this;
        };
        /**
         * Sends lead to leadspedia
         */
        this.post = () => {
            return this.addAction();
        };
        /**
         * Merges the input state with the existing lead state
         */
        this.merge = (state = {}) => {
            Object.assign(this._state, this.events.onMerge(state, this._state));
            this.events.onUpdate(this._state);
        };
        /**
         * Updates an existing lead in Leadspedia and optionally redistributes the
         * lead to a contract if the `redistribute` option is set.
         *
         * Redistributing a lead should only be for an existing lead.
         */
        this.update = (state = {}, updateOptions) => {
            this.merge(state);
            if (this._meta.leadId) {
                return this.addAction(updateOptions);
            }
            return this;
        };
        /**
         * Attempts to post a partial lead to leadspedia.
         */
        this.attemptPost = (state = {}) => {
            var _a, _b;
            this.merge(state);
            /* check for required fields */
            const hasAllRequiredFields = (_b = (_a = this.requiredPartialFields) === null || _a === void 0 ? void 0 : _a.every((field) => (this._state.hasOwnProperty(field)))) !== null && _b !== void 0 ? _b : false;
            if (this._meta.approved && hasAllRequiredFields) {
                return this.addAction();
            }
            return this;
        };
        /**
         * Flags a lead as approved and attempts to post a partial lead
         * if all required fields are present.
         */
        this.approveAndAttemptPost = () => {
            this._meta.approved = true;
            return this.attemptPost();
        };
        /**
         * Native toJSON method
         */
        this.toJSON = () => {
            return {
                __type: utils_1.LEAD_CACHE_IDENTIFIER,
                state: this.state,
                meta: this.meta,
            };
        };
        this.requiredPartialFields = requiredPartialFields;
        this.campaign = campaign;
        this.events = events;
        if ((0, utils_1.isCachedState)(initialState)) {
            this._state = initialState.state;
            Object.assign(this._meta, initialState.meta);
        }
        else {
            this._state = initialState;
            this._meta.testLead = test;
        }
        this.actions = Object.entries({
            baseActions: FetchActions_1.baseActions,
            leadIdActions: FetchActions_1.leadIdActions,
        }).reduce((acc, [actionType, methods]) => {
            methods.forEach((method) => {
                switch (actionType) {
                    case "baseActions": {
                        Object.assign(acc, {
                            [method]: client[method],
                        });
                        break;
                    }
                    case "leadIdActions": {
                        Object.assign(acc, {
                            [method]: (...args) => {
                                const fn = client[method];
                                if (!fn || typeof fn !== "function") {
                                    throw new Error(`Action method "${method}" is malformed!`);
                                }
                                if (this._meta.leadId) {
                                    fn(this._meta.leadId, ...args);
                                }
                                else {
                                    throw new Error(`You cannot perform action "${method}" with a lead that hasn't been posted`);
                                }
                            },
                        });
                        break;
                    }
                }
            });
            return acc;
        }, {});
    }
    get fields() {
        return {
            get: this.get,
            set: this.set,
        };
    }
    /**
     * Returns a copy of the current lead state
     */
    get state() {
        return (0, cloneDeep_1.default)(this._state);
    }
    /**
     * Returns an array containing all of the results from each
     * call to leadspedia
     */
    get allResults() {
        return (0, cloneDeep_1.default)(this.results);
    }
    /**
     * Collection of metadata representing various status values
     * of the current lead instance
     */
    get meta() {
        return (0, cloneDeep_1.default)(this._meta);
    }
}
exports.Lead = Lead;
//# sourceMappingURL=Lead.js.map